import { ChartTooltipContent, GappedChart } from "web-design-system";
import { formatAsPercentage, withScaledBarPct } from "creso-core/utils";
import { AllocationBarRow } from "web-core";
import {
  AllocationBreakdownBucket,
  AssetAllocationBucket,
} from "creso-core/types";
import { ASSET_ALLOCATION_BUCKET_COLORS } from "@crm/components/overview/asset-allocation";

export function mapAllocationBreakdownBuckets(
  buckets: AllocationBreakdownBucket[] = []
): AssetAllocationBucket[] {
  return buckets.map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    pct: bucket.pct,
    color: ASSET_ALLOCATION_BUCKET_COLORS[bucket.key],
    breakdownBy: bucket.breakdown_by,
    breakdown: bucket.sub_buckets
      .map((sub) => ({
        label: sub.label,
        pct: sub.pct,
      }))
      .filter((v) => v.pct > 0.1),
  }));
}

export function AssetAllocationReport({
  buckets,
}: {
  buckets: AssetAllocationBucket[];
}) {
  if (!buckets.length) return null;

  const segments = buckets.map((bucket) => ({
    key: bucket.key,
    value: bucket.pct,
    color: bucket.color,
    label: bucket.label,
  }));

  return (
    <>
      <GappedChart
        segments={segments}
        className="h-7 w-full"
        tooltipContent={
          <ChartTooltipContent
            indicator="dot"
            labelFormatter={() => "Asset Allocation"}
            valueFormatter={(val) => formatAsPercentage(val as string, "-", 2)}
          />
        }
      />
      <div className="flex flex-row flex-nowrap gap-4">
        {buckets.map((bucket) => {
          const breakdownRows = withScaledBarPct(bucket.breakdown ?? []);

          return (
            <div
              key={bucket.key}
              className="flex flex-1 min-w-0 flex-col gap-4"
            >
              <div className="flex items-center justify-between gap-2 rounded-lg bg-muted px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-3 h-3 rounded shrink-0"
                    style={{ backgroundColor: bucket.color }}
                  />
                  <span className="truncate">{bucket.label}</span>
                </div>
                <span className="font-semibold shrink-0">
                  {formatAsPercentage(bucket.pct, "-", 2)}
                </span>
              </div>
              <div className="px-4 flex flex-col gap-3">
                {breakdownRows.slice(0, 4).map((row, index) => (
                  <AllocationBarRow
                    key={`${row.label}-${index}`}
                    label={row.label}
                    pct={row.pct}
                    barPct={row.barPct}
                    color={bucket.color}
                    gradient
                    embedLabel
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
